import React, { useCallback, useEffect, useState } from 'react';
import { Col, Modal, Row } from '@flightstats/airframe-react';
import Input, { FormLabeledInputRowWrapper } from '../../js/components/forms/Input';
import { BodyCard } from '../../js/pages/components/styledComponent';
import { StyledCheckBox } from '../../js/components/forms/CheckBox';
import styles from './admin.module.css';
import cn from 'classnames';
import ActionRow from '../../js/pages/messageConfigurationContainer/ActionRow';
import { modalContentStyle } from '../../js/components/modal/Modal';
import { get, post } from '../../js/service/commonFetch';
import FullPageLoadingSpinner from '../../js/pages/components/fullPageLoadingSpinner';
import { AdminUserProps, createAdminUser, updateAdminUser } from '../../js/service/userAdminService';
import { ADMIN_USER_ROLES, API_ROUTES, resetPasswordMessage, SUPER_ADMIN_ACCOUNT_ID } from '../../js/constants';
import { isUndefinedOrNullOrEmpty } from '../../js/lib/filters';

interface GetRolesProps {
    roles: RolesProps[],
    selectedRoles: {
        ID: string;
        name: string;
        description: string;
    }[]
}
interface RolesProps {
    ID: string;
    name: string;
    description: string;
    value: boolean;
}

const inputSize = '40rem';
interface AccountAdminUserComponentProps {
    caller: string;
    modalClose: () => void;
    refreshUsers: (callback?: Function) => void;
    selectedAccount: {
        id: string;
        account: {
            name: string;
        };
        entitlements: {
            assignedOrgNodeId: string;
        };
    };
    selectedUserObj: {
        id?: string;
        userName: string;
        email: string;
        active: boolean;
        legacyId: string;
        createdAt: string;
        accountId: string;
        selectedRoles: string[];
        roles: string;
    };
}

interface GeneratePasswordProps {
    password: string;
}

const AccountAdminUserComponent: React.FC<AccountAdminUserComponentProps> = ({ caller, modalClose, selectedAccount, refreshUsers, selectedUserObj }) => {
    const [userObj, setUserObj] = useState<AdminUserProps>({ userName: '', password: '', email: '', active: true, accountId: '', customerName: '', userId: '', created: '' });
    const [rolesLists, setRolesLists] = useState<RolesProps[]>([]);
    const [loading, setLoading] = useState(false);
    const [resetPassword, setResetPassword] = useState(true);
    const [errorText, showErrorText] = useState('');

    const initializeUserObj = useCallback(() => {
        if (caller === 'edit') {
            setUserObj({
                id: selectedUserObj.id,
                userName: selectedUserObj.userName,
                email: selectedUserObj.email,
                active: selectedUserObj.active,
                userId: selectedUserObj.legacyId,
                created: new Date(selectedUserObj.createdAt).toLocaleDateString('en-GB'),
                accountId: selectedUserObj.accountId,
                customerName: selectedAccount.account.name,
            });
        } else {
            setUserObj((prevState) => ({
                ...prevState,
                accountId: selectedAccount.id,
                customerName: selectedAccount.account?.name,
                userName: '',
                password: '',
                email: '',
                active: true,
                userId: '',
                created: '',
            }));
        }
    }, [caller, selectedAccount, selectedUserObj]);

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            let result = await get<GetRolesProps>(API_ROUTES.getallroles((caller === 'add') ? 'newUser' : selectedUserObj.legacyId));
            result.roles = result.roles.map((role) => {
                if (caller === 'add' && role.name === ADMIN_USER_ROLES.REGULAR) {
                    return { ...role, value: true };
                } else if (caller === 'edit' && result.selectedRoles.findIndex((selectLine) => selectLine.name === role.name) !== -1) {
                    return { ...role, value: true };
                }
                return { ...role, value: false };
            });
    
            if (selectedAccount.id !== SUPER_ADMIN_ACCOUNT_ID) {
                result.roles = result.roles.filter(
                    (role) =>
                        role.name === ADMIN_USER_ROLES.REGULAR || role.name === ADMIN_USER_ROLES.SYSADMIN
                );
            }
            setRolesLists(result.roles);
        } catch (error) {
            showErrorText('Error fetching roles.');
        } finally {
            setLoading(false);
        }
    }, [caller, selectedAccount, selectedUserObj]);

    useEffect(() => {
        if (resetPassword && selectedAccount?.id && caller !== '') {
            initializeUserObj();
            fetchRoles();
        }
    }, [resetPassword, selectedAccount, initializeUserObj, fetchRoles, caller]);

    const onChange = (fieldName: string, value: string | boolean) => {
        setUserObj(prevState => ({ ...prevState, [fieldName]: value }));
    }

    const onSave = async (callback?: Function): Promise<void> => {
        setLoading(true);
        const selectedRoles = rolesLists.filter((role) => role.value).map((role) => role.name);
        let defaultRole;
        if (selectedRoles.includes(ADMIN_USER_ROLES.CONDUCIVE) || selectedRoles.includes(ADMIN_USER_ROLES.SUDOLOGIN)) {
            defaultRole = ADMIN_USER_ROLES.SUPERADMIN;
        } else if (selectedRoles.includes(ADMIN_USER_ROLES.SYSADMIN)) {
            defaultRole = ADMIN_USER_ROLES.ACCOUNTADMIN;
        } else {
            defaultRole = ADMIN_USER_ROLES.AGENT;
        }
        const userPayload = {
            ...userObj,
            accountId: selectedAccount.id,
            customerName: selectedAccount.account?.name,
            roles: defaultRole,
            selectedRoles,
            assignedOrgNodeId: selectedAccount.entitlements.assignedOrgNodeId,
        };
        
        if (
            isUndefinedOrNullOrEmpty(userPayload.email) ||
            isUndefinedOrNullOrEmpty(userPayload.userName) ||
            selectedRoles.length === 0
        ) {
            showErrorText('Email Address, Username and Roles are mandatory.');
            setLoading(false);
            return;
        }
    
        try {
            if (caller === 'add') {
                await createAdminUser(userPayload);
            } else {
                await updateAdminUser({...userPayload, editUserId: selectedUserObj.id || ''});
            }
            if (callback === undefined) {
                resetState();
            }
            refreshUsers(callback);
        } catch (error) {
            showErrorText(error instanceof Error ? error.message : 'Error saving user.');
        } finally {
            setLoading(false);
        }
    };

    const resetState = () => {
        modalClose();
        showErrorText('');
        setResetPassword(true);
    }

    return (
        <Modal isOpen={caller !== ''} contentStyle={modalContentStyle}>
            {loading && <FullPageLoadingSpinner></FullPageLoadingSpinner>}
            <div className="issue-assign issue-resolution-modal modal-dialog modal-lg">
                <div className="title">
                    {caller === "edit" ? "EDIT USER" : "CREATE NEW USER"}
                    <a onClick={resetState}>
                        <i className="cancel pull-right glyphicon icon-white glyphicon-remove"></i>
                    </a>
                </div>
                <div className={cn(styles.modalBorder)}>
                    <BodyCard>
                        <Row>
                            <Col>
                                <div className={cn(styles.blink)}>
                                    {errorText}
                                </div>
                                {caller === 'edit' && <>
                                    <FormLabeledInputRowWrapper
                                        elementId="userId"
                                        label="User ID:"
                                        inputWidth={inputSize}
                                    >
                                        <Input
                                            id="userId"
                                            name="userId"
                                            value={userObj.userId}
                                            onChange={e => onChange('userId', e?.currentTarget?.value)}
                                            disabled={true}
                                        />
                                    </FormLabeledInputRowWrapper>
                                    <FormLabeledInputRowWrapper
                                        elementId="created"
                                        label="Created:"
                                        inputWidth={inputSize}
                                    >
                                        <Input
                                            id="created"
                                            name="created"
                                            value={userObj.created}
                                            onChange={e => onChange('created', e?.currentTarget?.value)}
                                            disabled={true}
                                        />
                                    </FormLabeledInputRowWrapper>
                                </>}
                                <FormLabeledInputRowWrapper
                                    elementId="email"
                                    label="Email Address:"
                                    inputWidth={inputSize}
                                >
                                    <Input
                                        id="email"
                                        name="email"
                                        value={userObj.email}
                                        onChange={e => onChange('email', e?.currentTarget?.value)}
                                    />
                                </FormLabeledInputRowWrapper>
                                <FormLabeledInputRowWrapper
                                    elementId="userName"
                                    label="Username:"
                                    inputWidth={inputSize}
                                >
                                    <Input
                                        id="userName"
                                        name="userName"
                                        value={userObj.userName}
                                        onChange={e => onChange('userName', e?.currentTarget?.value)}
                                    />
                                </FormLabeledInputRowWrapper>
                                <FormLabeledInputRowWrapper
                                    elementId="password"
                                    label="Password:"
                                    inputWidth={inputSize}
                                >
                                    <div className={cn(styles.displayFlex)}>
                                        {caller === 'edit' ? <button
                                            type="button"
                                            className="btn btn-warning margin-left-5"
                                            onClick={() => {
                                                setResetPassword(false);
                                                onSave(() => {
                                                    post(API_ROUTES.resetPassword, {email: userObj.email}).then(() => {
                                                        setLoading(false);
                                                        showErrorText(resetPasswordMessage(userObj.email));
                                                    });
                                                });
                                            }}
                                        >
                                            Reset Password / Save User / Send Email
                                        </button> : <>
                                            <Input
                                                id="password"
                                                name="password"
                                                value={userObj.password}
                                                disabled={true}
                                                onChange={e => onChange('password', e?.currentTarget?.value)}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-warning margin-left-5"
                                                onClick={() => {
                                                    get(API_ROUTES.generatepassword).then((result) => {
                                                        const passwordResult = result as GeneratePasswordProps;
                                                        setUserObj(prevState => ({ ...prevState, password: passwordResult.password }));
                                                    });
                                                }}
                                            >
                                                Generate Password
                                            </button>
                                        </>}
                                    </div>
                                </FormLabeledInputRowWrapper>
                                <div>
                                    <StyledCheckBox
                                        id={'active'}
                                        label="Active"
                                        checked={userObj.active}
                                        onChange={() => onChange('active', !userObj.active)}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </BodyCard>
                    <BodyCard>
                        <Row>
                            <Col>
                                <div className={cn(styles.titleText, styles.textTransformUpper, styles.headerMarginLeft, styles.headerMarginBottom)}>{'Account Settings'}</div>
                                <FormLabeledInputRowWrapper
                                    elementId="accountId"
                                    label="Account ID:"
                                    inputWidth={inputSize}
                                >
                                    <Input
                                        id="accountId"
                                        name="accountId"
                                        value={userObj.accountId}
                                        disabled={true}
                                        onChange={e => onChange('accountId', e?.currentTarget?.value)}
                                    />
                                </FormLabeledInputRowWrapper>
                                <FormLabeledInputRowWrapper
                                    elementId="customerName"
                                    label="Customer Name:"
                                    inputWidth={inputSize}
                                >
                                    <Input
                                        id="customerName"
                                        name="customerName"
                                        value={userObj.customerName}
                                        onChange={e => onChange('customerName', e?.currentTarget?.value)}
                                        disabled={true}
                                    />
                                </FormLabeledInputRowWrapper>
                            </Col>
                        </Row>
                    </BodyCard>
                    <BodyCard>
                        <Row>
                            <Col>
                                <div className={cn(styles.headerMarginLeft)}>
                                    <div className={cn(styles.titleText, styles.textTransformUpper, styles.headerMarginBottom)}>{'User Roles'}</div>
                                    <div className='col-sm-12 p-0'>
                                        <div className='col-sm-6 p-0'>
                                            <div className={cn(styles.titleText)}>{'Available Roles'}</div>
                                            {rolesLists.map((line: RolesProps) => {
                                                return <div>
                                                    <StyledCheckBox
                                                        id={line.ID}
                                                        label={line.name}
                                                        checked={line.value || false}
                                                        onChange={() => {
                                                            setRolesLists(rolesLists.map((roles: RolesProps) => 
                                                                roles.ID === line.ID ? { ...roles, value: !roles.value } : roles
                                                            ));
                                                        }}
                                                    />
                                                </div>
                                            })}
                                        </div>
                                        <div className='col-sm-6 p-0'>
                                            <div className={cn(styles.titleText)}>{'Selected Roles'}</div>
                                            {rolesLists.filter((roles: RolesProps) => roles.value).map((line: RolesProps) => {
                                                return <div>{line.name}</div>
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <ActionRow
                                    id="nameButtons"
                                    onCancel={() => resetState()}
                                    onSave={() => onSave()}
                                />
                            </Col>
                        </Row>
                    </BodyCard>
                </div>
            </div>
        </Modal>
    );
};

export default AccountAdminUserComponent;